import { useNectarProject } from '@/features/nectar/api'
import { createPrivateSession, createPublicSession, extractApiError } from '@/features/nectar/api/conversations-api'
import { Button } from '@/ui-kit'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useState } from 'react'
import { toast } from 'sonner'
import { NoCapacityStep } from './NoCapacityStep'

export const ConversationModeStep = ({ projectId, onSuccess }: { projectId: string; onSuccess: () => void }) => {
  const { data: project } = useNectarProject(projectId)
  const [pendingMode, setPendingMode] = useState<'private' | 'public' | null>(null)
  const [noCapacity, setNoCapacity] = useState(false)

  const handleHostPrivate = async () => {
    setPendingMode('private')
    try {
      const result = await createPrivateSession(projectId)
      if ('status' in result && result.status === 'no_capacity') {
        setNoCapacity(true)
      } else {
        onSuccess()
      }
    } catch (err) {
      toast.error(extractApiError(err, t`Failed to create private conversation`))
    } finally {
      setPendingMode(null)
    }
  }

  const handleJoinPublic = async () => {
    setPendingMode('public')
    try {
      const result = await createPublicSession(projectId)
      if ('status' in result && result.status === 'no_capacity') {
        setNoCapacity(true)
      } else {
        onSuccess()
      }
    } catch (err) {
      toast.error(extractApiError(err, t`Failed to join public conversation`))
    } finally {
      setPendingMode(null)
    }
  }

  if (noCapacity) {
    return <NoCapacityStep />
  }

  return (
    <>
      {project?.description && (
        <div className="text-gray-90 text-body-2 -mt-2 max-w-[800px]">{project.description}</div>
      )}
      <div className="m-auto mt-4 flex min-h-[300px] w-full max-w-[800px] flex-col items-center gap-4">
        <div
          className="max-mobile:flex-col flex h-full min-h-[280px] w-full gap-6 rounded-2xl border border-solid border-gray-100 p-6"
          style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
        >
          <div className="max-mobile:w-full max-mobile:border-r-0 max-mobile:border-b-0 flex w-1/2 flex-col gap-2 border-r border-solid border-gray-100 p-4 pr-8">
            <div className="text-body-2 text-gray-90 tracking-wider uppercase">
              <Trans>&#8226; Host</Trans>
            </div>
            <div className="text-subtitle text-gray-70 font-semibold">
              <Trans>Create private conversation</Trans>
            </div>
            <div className="text-gray-80">
              <Trans>Start a private conversation and add the person you want to talk to.</Trans>
            </div>
            <Button
              variant="default"
              className="mt-auto w-full"
              onClick={handleHostPrivate}
              isLoading={pendingMode === 'private'}
              isDisabled={pendingMode !== null && pendingMode !== 'private'}
            >
              <Trans>Create</Trans>
            </Button>
          </div>
          <div className="max-mobile:w-full flex w-1/2 flex-col gap-2 p-4">
            <div className="text-body-2 text-gray-90 tracking-wider uppercase">
              <Trans>&#8226; Join</Trans>
            </div>
            <div className="text-subtitle text-gray-70 font-semibold">
              <Trans>Join public conversation</Trans>
            </div>
            <div className="text-gray-80">
              <Trans>Get paired with another contributor for a live one-on-one voice conversation.</Trans>
            </div>
            <Button
              variant="green"
              className="mt-auto w-full bg-transparent!"
              onClick={handleJoinPublic}
              isLoading={pendingMode === 'public'}
              isDisabled={pendingMode !== null && pendingMode !== 'public'}
            >
              <Trans>Join</Trans>
            </Button>
          </div>
        </div>
        {project?.rules && (
          <div className="mt-6 flex flex-col gap-2 self-start">
            <div className="text-gray-80 font-semibold">
              <Trans>Rules:</Trans>
            </div>
            <div className="text-gray-90 text-body-2 leading-6 whitespace-pre-line">{project.rules}</div>
          </div>
        )}
      </div>
    </>
  )
}
